import {Formik, FormikProps, FormikValues} from 'formik';
import {filter, forEach, some} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    AssignmentSelectors, Button, Card, DataWrapper, InputFormik,
    MultiInputFormik, PredefinedNotifierType, UnsavedChangesLeavePrompt
} from 'components';
import {ValidationCode} from 'enums';
import {useApiData} from 'hooks';
import {AccountAssignmentInfo, Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {apiUtils, dataUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.SESSION_RESULT;
const SHOW_BOTTOM_SUBMIT_AFTER_COUNT = 8;

type FormFields = {
    resultValues: string[];
}

const FormTemplate: React.FC<FormikProps<FormFields>> = ({values, initialValues, setFieldError, submitForm}: FormikProps<FormFields>) => {
    const {t} = useTranslation();
    const isItemValid = useCallback((item: string) => !!item, []);
    const isItemDuplicated = useCallback((item: string) => filter(values.resultValues, (it) => it === item).length > 1, [values]);
    const hasInvalidItems = useMemo(() => some(values.resultValues, (item) => !item || isItemDuplicated(item)), [values, isItemDuplicated]);
    const isSaveDisabled = useMemo<boolean>(() => hasInvalidItems ||
        dataUtils.isEqualValues(values.resultValues, initialValues.resultValues, true), [hasInvalidItems, values, initialValues]);

    const getErrorMessage = useCallback((item: string) => {
        let error;
        if (!item) {
            error = {code: ValidationCode.Required};
        } else if (isItemDuplicated(item)) {
            error = {code: ValidationCode.Duplicated};
        }
        return error ? apiUtils.getErrorMessageByCode(t, error) : null;
    }, [isItemDuplicated, t]);

    useEffect(() => {
        forEach(values.resultValues, (item, index) => {
            const errorMessage = getErrorMessage(item);
            setFieldError(`resultValues[${index}]`, errorMessage);
        });
    }, [getErrorMessage, setFieldError, t, values.resultValues]);

    return (
        <Card>
            <div className='tw-text-right tw-mb-4'>
                <Button
                    color='primary'
                    disabled={isSaveDisabled}
                    onClick={submitForm}
                >
                    {t(T_KEYS.BUTTON_SAVE)}
                </Button>
            </div>
            <MultiInputFormik
                name='resultValues'
                itemsPath='resultValues'
                defaultValue=''
                values={values}
                isValid={isItemValid}
            >
                {({index, item, last, path, renderActions}) => (
                    <div key={index} className='tw-flex tw-items-baseline tw-mb-4'>
                        <InputFormik
                            validate={!last}
                            className='tw-flex-grow'
                            name={path}
                            label={t(last ? T_KEYS.LABELS.NEW : T_KEYS.LABELS.FILLED)}
                        />
                        <div className='tw-w-10 -lg:tw-mt-6'>{renderActions({index, item})}</div>
                    </div>
                )}
            </MultiInputFormik>
            {values.resultValues && values.resultValues.length > SHOW_BOTTOM_SUBMIT_AFTER_COUNT &&
                <div className='tw-text-right tw-mb-3 tw-my-8'>
                    <Button
                        color='primary'
                        disabled={isSaveDisabled}
                        onClick={submitForm}
                    >
                        {t(T_KEYS.BUTTON_SAVE)}
                    </Button>
                </div>}
            <UnsavedChangesLeavePrompt values={values} initialValues={initialValues} />
        </Card>
    );
};

type Props = {
    groups: Group[];
    fetchData: (assignment: AccountAssignmentInfo) => Promise<string[]>;
    saveData: (assignment: AccountAssignmentInfo, values: string[]) => Promise<string[]>;
};

export const SessionResultInformationValues: React.FC<Props> = ({groups, fetchData, saveData}: Props) => {
    const [assignment, setAssignment] = useState<AccountAssignmentInfo>({
        groupId: '',
        subgroupId: ''
    });

    const {
        data: resultValues,
        send: getResultValues,
        loading: fetchLoading
    } = useApiData<string[], [AccountAssignmentInfo?]>(fetchData, {notificationType: PredefinedNotifierType.FetchError});

    const [initialValue, setInitialValue] = useState<FormFields>({resultValues});

    const onSaveSuccess = useCallback((values: string[]) => {
        setInitialValue({resultValues: values});
    }, []);

    const {send: save, loading: saveLoading} = useApiData(
        saveData, {onSuccess: onSaveSuccess, notificationType: PredefinedNotifierType.Save});

    const handleSubmit = useCallback((formValues: FormikValues) => {
        save(assignment, formValues.resultValues);
    }, [assignment, save]);

    useEffect(() => {
        setInitialValue({resultValues});
    }, [resultValues]);

    useEffect(() => {
        getResultValues(assignment);
    }, [getResultValues, assignment]);

    return (
        <div className='tw-relative'>
            <DataWrapper isLoading={fetchLoading || saveLoading} hideContentOnLoading={false}>
                <div className='tw-mb-4 xl:tw-w-1/2'>
                    <AssignmentSelectors
                        grid
                        groups={groups}
                        selectedGroupId={assignment?.groupId}
                        selectedSubgroupId={assignment?.subgroupId}
                        onChange={(groupId, subgroupId) => setAssignment({groupId: groupId || '', subgroupId: subgroupId || ''})}
                    />
                </div>
                <Formik
                    enableReinitialize
                    validateOnChange={false}
                    validateOnBlur={false}
                    initialValues={initialValue}
                    onSubmit={handleSubmit}
                >
                    {(props) => <FormTemplate {...props} />}
                </Formik>
            </DataWrapper>
        </div>
    );
};